import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IamService } from '@app/core/services/iam.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { detectDelimiter } from '@app/shared/helpers/csv';
import { buildDates, formatDate } from '@app/shared/helpers/helpers';
import { isEmail } from '@app/shared/helpers/validators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import moment from 'moment';
import { NgxCsvParser } from 'ngx-csv-parser';
import { forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { MembersActions } from '../actions';
import * as fromMembers from '../reducers/members.reducer';

const SCIMGroupName = 'SCIM';

@Injectable()
export class MembersEffects {

  loadMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMember),
      concatMap((action) => {
        return forkJoin([this.iamService.getTeamUser(action.teamId, action.userId), this.iamService.listTeamUserRoles(action.teamId, action.userId)]).pipe(
          map(([resMember, resUserRoles]) => {
            const { user } = resMember;
            const { roles } = resUserRoles;
            return MembersActions.loadMemberSuccess({ member: { ...user, isRoot: !!roles.find(role => role.name === Constant.administratorRoleName) } });
          }),
          catchError(error => { this.router.navigateByUrl('/account/team/' + action.teamId + '/members'); return of(MembersActions.loadMemberFailure({ error: error.message })) }
          )
        );
      }))
  );

  loadAccountMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.LoadAccountMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]: any[]) => {
        return this.iamService.listUsers().pipe(
          map(res => {
            const SCIMUserName = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
            const { users } = res;
            const usersWithoutSCIMUser = users.filter(user => user.username !== SCIMUserName);
            return MembersActions.LoadAccountMembersSuccess({ members: usersWithoutSCIMUser });
          }),
          catchError(error =>
            of(MembersActions.LoadAccountMembersFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberRoles),
      concatMap((action) => {
        return forkJoin([
          this.iamService.listTeamUserRoles(action.teamId, action.userId).pipe(map((res) => res), catchError(() => of({ roles: [] }))),
          this.iamService.listTeamRoles(action.teamId)
        ]).pipe(
          map(([{ roles: userRoles }, { roles: teamRoles }]) => {
            const filterUserRoles = userRoles.filter(userRole => userRole.name.startsWith(action.teamId));
            const parsedUserRoles = filterUserRoles.map(role => ({ ...role, translatedName: this.translateService.instant(role.name.split('$')[1].toUpperCase()) }));
            const attributableRoles = teamRoles.filter(teamRole => !userRoles.some(userRole => userRole.name === teamRole.name));
            const parsedAttributableRoles = attributableRoles.map(role => ({ ...role, translatedName: this.translateService.instant(role.name.split('$')[1].toUpperCase()) }));
            return MembersActions.loadMemberRolesSuccess({ roles: parsedUserRoles, attributableRoles: parsedAttributableRoles });
          }),
          catchError(error =>
            of(MembersActions.loadMemberRolesFailure({ error: error.message }))
          )
        );
      }))
  );

  loadTeamRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadTeamRoles),
      switchMap(({ teamId }) => {
        return this.iamService.listTeamRoles(teamId).pipe(
          map(({ roles }) => {
            return MembersActions.loadTeamRolesSuccess({ roles });
          }),
          catchError(error =>
            of(MembersActions.loadTeamRolesFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberGroups),
      concatMap((action) => {
        return forkJoin(
          [
            this.iamService.listTeamUserGroups(action.teamId, action.userId),
            this.iamService.listTeamGroups(action.teamId),
          ]
        ).pipe(
          map(([resGroups, resAccountGroups]: any[]) => {
            const { groups: memberGroups } = resGroups;
            const { groups: accountGroups } = resAccountGroups;
            const filteredAccountGroups = accountGroups.filter(accountGroup => accountGroup.name !== SCIMGroupName && !accountGroup.provisioned);
            const attributableGroups = filteredAccountGroups.filter(accountGroup => !memberGroups.find(memberGroup => accountGroup.id === memberGroup.id));
            return MembersActions.loadMemberGroupsSuccess({ groups: memberGroups, attributableGroups });
          }),
          catchError(error =>
            of(MembersActions.loadMemberGroupsFailure({ error: error.message }))
          )
        );
      }))
  );

  loadMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]) =>
        this.iamService.listTeamUsers(action.teamId).pipe(
          map((response: any) => {
            const SCIMUsername = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
            const { users } = response;
            const usersFiltered = users.filter(user => user.username !== SCIMUsername && user.id !== identity.id);
            return MembersActions.loadMembersSuccess({ members: usersFiltered });
          }),
          catchError(error =>
            of(MembersActions.loadMembersFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadAttributableGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadAttributableGroups),
      switchMap(() =>
        this.iamService.listGroups().pipe(
          map(({ groups }: any) => {
            const attributableGroups = groups.filter(accountGroup => accountGroup.name !== SCIMGroupName && !accountGroup.provisioned);
            return MembersActions.loadAttributableGroupsSuccess({ groups: attributableGroups });
          }),
          catchError(error =>
            of(MembersActions.loadAttributableGroupsFailure({ error: error.message }))
          )
        )
      )
    )
  );


  addMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.addMember),
      withLatestFrom(this.store$.pipe(select(fromRoot.getSelectedLanguage))),
      concatMap(([action, language]) => {
        const { member, lastName, firstName, teamId, invited, role, authenticationMode } = action;
        if (member?.id) {
          return this.iamService.attachUserToTeam(teamId, member.id).pipe(concatMap(() => {
            return this.iamService.attachTeamUserRole(teamId, member.id, role.name).pipe(map(() => {
              return MembersActions.addMemberSuccess({ member });
            }));
          }),
            catchError((error: HttpErrorResponse) => of(MembersActions.addMemberFailure({ error: error.error }))));
        } else {
          const userToCreate = {
            username: member.username,
            firstName,
            lastName,
            authenticationMode,
            language,
          };
          return this.iamService.createUser(userToCreate).pipe(concatMap(({ user: userCreated }) => {
            return this.iamService.attachUserToTeam(teamId, userCreated.id).pipe(concatMap(() => {
              return this.iamService.attachTeamUserRole(teamId, userCreated.id, role.name).pipe(concatMap(() => {
                if (invited) {
                  return this.iamService.inviteTeamUser(teamId, userCreated.id).pipe(map(() => {
                    return MembersActions.addMemberSuccess({ member });
                  }));
                } else {
                  return [MembersActions.addMemberSuccess({ member })];
                }
              }));
            }));
          }),
            catchError((error: HttpErrorResponse) => of(MembersActions.addMemberFailure({ error: error.error }))));
        }
      })
    )
  );

  initImportMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.initImportMembers),
      map((action) => {
        return MembersActions.checkContentFile({ teamId: action.teamId, fileMembers: action.fileMembers, csvContent: action.csvContent });
      })
    )
  );


  checkContentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.checkContentFile),
      switchMap((action) => {
        const delimiter = detectDelimiter(action.csvContent);
        return this.iamService.listTeamGroups(action.teamId).pipe(concatMap(({ groups: accountGroups }) => {
          return this.ngxCsvParser.parse(action.fileMembers[0], { header: false, delimiter }).pipe(concatMap((rows: any[]) => {
            // Removing headers
            rows.shift();
            let errorDetails = '';
            if (rows.length > 0) {
              // Looking for error in CSV and building error report
              for (let index = 0; index < rows.length; index++) {
                let errorRow = '';
                if (rows[index].length === 5) {
                  const [username, authenticationMode, groups] = rows[index];
                  const trimmedUsername = username.trim();
                  const trimmedAuthenticationMode = authenticationMode.trim();
                  if (!isEmail(trimmedUsername)) {
                    errorRow += ` Invalid email : ${username ? '\'' + username + '\'' : 'Empty email'}.`;
                  } else if (rows.find((row) => row.username === username)) {
                    errorRow += ` Same email on another row`;
                  }
                  if (!(trimmedAuthenticationMode === Constant.signinMode.sso || trimmedAuthenticationMode === Constant.signinMode.standard)) {
                    errorRow += ` Invalid authentication mode : '${trimmedAuthenticationMode}'. Choose either '${Constant.signinMode.sso}' or '${Constant.signinMode.standard}' (case sensitive).`;
                  }
                  const parsedGroupNames = groups.split(',').map(group => group.trim());
                  if (parsedGroupNames?.length > 0) {
                    parsedGroupNames.forEach(groupName => {
                      if (!accountGroups.find(group => group.name === groupName)) {
                        errorRow += ` Invalid group name : ${groupName ? '\'' + groupName + '\'' : 'undefined'}.`;
                      }
                    });
                  } else {
                    errorRow += ` Empty or invalid group list. At least one group is required. Check if group names are separated by a dash.`;
                  }
                } else {
                  errorRow += `Row format is incorrect. Check for extra columns or unescaped commas.`;
                }
                if (!!errorRow) {
                  errorDetails += `Row ${index} - ${errorRow} \n`;
                }
              }
            } else {
              errorDetails += `Empty CSV`;
            }
            if (errorDetails.length > 0) {
              const error = { date: moment().format('YYYY-MM-DD'), details: errorDetails };
              return [MembersActions.checkContentFileFailure({ error })];
            }
            // Building array of members to create
            const members = rows.map(([username, authenticationMode, groups, firstName, lastName]) => {
              return {
                username: username.trim(),
                authenticationMode: authenticationMode.trim(),
                groups: groups.split(',').map(group => group.trim()),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
              }
            });
            return [MembersActions.importMembers({ teamId: action.teamId, members })];
          }), catchError((error: any) => [MembersActions.checkContentFileFailure({ error })])
          )
        }));
      })
    )
  );

  importMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.importMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getSelectedLanguage))),
      switchMap(async ([action, language]) => {
        const { teamId, members } = action;
        const { groups: accountGroups } = await this.iamService.listTeamGroups(teamId).toPromise();
        let errorDetails = '';
        let importedMembers = [];
        const { users: existingMembers } = await this.iamService.listUsers().toPromise();
        for (const member of members) {
          const existingMember = existingMembers.find(item => member.username === item.username);
          if (existingMember) {
            const groupsToAttach = accountGroups.filter(group => member.groups.find(groupName => groupName === group.name));
            await Promise.all(
              groupsToAttach.map(group => this.iamService.attachTeamGroupUser(teamId, group.id, existingMember.id).toPromise()
                .catch((error: HttpErrorResponse) => {
                  if (error.status === 409) {
                    errorDetails += `Failed to attach member '${member.username}' to group '${group.name}'. User already attached to this group.\n`;
                  }
                })
              )
            );
            importedMembers = [...importedMembers, existingMember];
            this.store$.dispatch(MembersActions.UpdateImportMembersStatus({ importedMembers }));
          } else {
            const memberToCreate = { ...member, language };
            const resMemberCreated = await this.iamService.createUser(memberToCreate).toPromise()
              .catch((error: HttpErrorResponse) => {
                if (error.status === 409) {
                  errorDetails += `Failed to create member '${member.username}'. Email already in use. User has not been attached to corresponding groups.\n`;
                }
              });
            if (resMemberCreated && resMemberCreated.user) {
              const groupsToAttach = accountGroups.filter(group => member.groups.find(groupName => groupName === group.name));
              await Promise.all(groupsToAttach.map(group => this.iamService.attachTeamGroupUser(teamId, group.id, resMemberCreated.user.id).toPromise()));
              importedMembers = [...importedMembers, existingMember];
              if (resMemberCreated.user.authenticationMode === Constant.signinMode.standard) {
                await this.iamService.inviteTeamUser(teamId, resMemberCreated.user.id).toPromise();
              }
              this.store$.dispatch(MembersActions.UpdateImportMembersStatus({ importedMembers }));
            }
          }
        }
        if (errorDetails.length > 0) {
          const nonBlockingError = { date: moment().format('YYYY-MM-DD'), details: errorDetails };
          return MembersActions.importMembersSuccess({ members: importedMembers, nonBlockingError });
        } else {
          return MembersActions.importMembersSuccess({ members: importedMembers });
        }
      })
    )
  );

  attachMemberToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.attachMemberToGroup),
      switchMap(action => {
        const { member, group, teamId } = action;
        return this.iamService.attachTeamGroupUser(teamId, group.id, member.id).pipe(
          concatMap(() => {
            return [
              MembersActions.attachMemberToGroupSuccess(),
              MembersActions.loadMemberGroups({ teamId, userId: member.id }),
            ];
          }),
          catchError(error =>
            of(MembersActions.attachMemberToGroupFailure({ error: error.message }))
          )
        );
      })));

  detachMemberFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.detachMemberFromGroup),
      switchMap(action => {
        const { group, member, teamId } = action;
        return this.iamService.detachTeamGroupUser(teamId, group.id, member.id).pipe(
          concatMap(() => {
            return [
              MembersActions.detachMemberFromGroupSuccess(),
              MembersActions.loadMemberGroups({ teamId, userId: member.id }),
            ];
          }),
          catchError(error =>
            of(MembersActions.detachMemberFromGroupFailure({ error: error.message }))
          )
        );
      })));

  attachRoleToMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.attachRoleToMember),
      switchMap(action => {
        const { role, userId, teamId } = action;
        return this.iamService.listTeamUserRoles(teamId, userId).pipe(concatMap(({ roles }) => {
          if (roles?.length) {
            return this.iamService.attachTeamUserRole(teamId, userId, role.name).pipe(
              concatMap(() => {
                return [
                  MembersActions.attachRoleToMemberSuccess(),
                  MembersActions.loadMemberRoles({ teamId, userId }),
                ];
              }),
              catchError(error =>
                of(MembersActions.attachRoleToMemberFailure({ error: error.message }))
              )
            );
          } else {
            return this.iamService.attachUserToTeam(teamId, userId).pipe(concatMap(() => {
              return this.iamService.attachTeamUserRole(teamId, userId, role.name).pipe(
                concatMap(() => {
                  return [
                    MembersActions.attachRoleToMemberSuccess(),
                    MembersActions.loadMemberRoles({ teamId, userId }),
                  ];
                }),
                catchError(error =>
                  of(MembersActions.attachRoleToMemberFailure({ error: error.message }))
                )
              );
            }));
          }
        }));
      })));

  detachRoleFromMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.detachRoleFromMember),
      switchMap(action => {
        const { role, userId, teamId } = action;
        return this.iamService.detachTeamUserRole(teamId, userId, role.name).pipe(
          concatMap(() => {
            return this.iamService.listTeamUserRoles(teamId, userId).pipe(concatMap(({ roles }) => {
              const teamUsersRoles = roles.filter(teamRole => teamRole.name.startsWith(teamId));
              if (!teamUsersRoles.length) {
                return this.iamService.detachUserFromTeam(teamId, userId).pipe(concatMap(() => {
                  return [
                    MembersActions.detachRoleFromMemberSuccess(),
                    MembersActions.loadMemberRoles({ teamId, userId }),
                  ];
                }));
              } else {
                return [
                  MembersActions.detachRoleFromMemberSuccess(),
                  MembersActions.loadMemberRoles({ teamId, userId }),
                ];
              }
            }));
          }),
          catchError(error =>
            of(MembersActions.detachRoleFromMemberFailure({ error: error.message }))
          )
        );
      })));

  deleteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.deleteMember),
      switchMap((action: any) => {
        return forkJoin([
          this.iamService.listTeamUserRoles(action.teamId, action.member.id),
          this.iamService.listTeamUserGroups(action.teamId, action.member.id),
        ]).pipe(
          concatMap(([{ roles: teamUserRoles }, { groups: teamUserGroups }]) => {
            const batchDetachUserTeamRoles = teamUserRoles.map(role => this.iamService.detachTeamUserRole(action.teamId, action.member.id, role.name));
            const batchDetachUserGroups = teamUserGroups.filter(group => !group.provisioned).map(group => this.iamService.detachTeamGroupUser(action.teamId, group.id, action.member.id));
            return forkJoin([...batchDetachUserTeamRoles, ...batchDetachUserGroups]).pipe(defaultIfEmpty(null), concatMap(() => {
              if (batchDetachUserTeamRoles.length) {
                return this.iamService.detachUserFromTeam(action.teamId, action.member.id).pipe(concatMap(() => {
                  return [MembersActions.deleteMemberSuccess(), MembersActions.loadMembers({ teamId: action.teamId })];
                }));
              } else {
                return [MembersActions.deleteMemberSuccess(), MembersActions.loadMembers({ teamId: action.teamId })];
              }
            }));
          }),
          catchError(error =>
            of(MembersActions.deleteMemberFailure({ error: error.message }))
          )
        );
      })
    )
  );

  updateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.updateMember),
      withLatestFrom(this.store$.pipe(select(fromMembers.getMemberDetails))),
      concatMap(([action, memberDetails]: any[]) => {
        if (memberDetails?.provisioned) {
          return [MembersActions.updateMemberFailure({ error: `Member is managed by provisioning system, can't modify related informations` })];
        }
        return this.iamService.updateTeamUser(action.teamId, memberDetails.id, action.data).pipe(
          concatMap(response => {
            return [
              MembersActions.updateMemberSuccess({ member: response.user }),
              MembersActions.loadMember({ teamId: action.teamId, userId: response.user.id })
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            return [MembersActions.updateMemberFailure({ error: error.error })];
          }
          )
        );
      }
      )
    )
  );

  inviteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.inviteMember),
      concatMap((action) =>
        this.iamService.inviteTeamUser(action.teamId, action.userId).pipe(
          map((response: any) => {
            return MembersActions.inviteMemberSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            return [MembersActions.inviteMemberFailure({ error: error.message })];
          }
          )
        )
      )
    )
  );

  switchMemberRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.switchMemberRole),
      switchMap(action => {
        return forkJoin([
          this.iamService.detachUserRole(action.member.id, action.roleToDettach.name),
          this.iamService.attachUserRole(action.member.id, action.roleToAttach.name)
        ]).pipe(
          map((response: any) => {
            return MembersActions.switchMemberRoleSuccess();
          }),
          catchError(error =>
            of(MembersActions.switchMemberRoleFailure({ error }))
          )
        );
      }))
  );

  loadMemberTransferCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberTransferCount),
      concatMap((action) => {
        const { teamId, user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'transfers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamUserMetricStatistic({ metricName, statisticName, teamId, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const transferCountSeries = statistic.data;
            return MembersActions.loadMemberTransferCountSuccess({ transferCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberTransferCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberSizeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberSizeCount),
      concatMap((action) => {
        const { teamId, user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'bytesUploaded';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamUserMetricStatistic({ metricName, statisticName, teamId, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const sizeCountSeries = statistic.data;
            return MembersActions.loadMemberSizeCountSuccess({ sizeCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberSizeCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadMemberFileCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembersActions.loadMemberFileCount),
      concatMap((action) => {
        const { teamId, user, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'files';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamUserMetricStatistic({ metricName, statisticName, teamId, userId: user, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const fileCountSeries = statistic.data;
            return MembersActions.loadMemberFileCountSuccess({ fileCountSeries });
          }),
          catchError(error =>
            of(MembersActions.loadMemberFileCountFailure({ error: error.message }))
          ));
      })
    )
  );

  constructor(
    private actions$: Actions,
    private translateService: TranslateService,
    private iamService: IamService,
    private tokenService: TokenService,
    private router: Router,
    private store$: Store<fromRoot.State & fromMembers.State>,
    private ngxCsvParser: NgxCsvParser,
  ) { }

}
